import React, { useCallback, useState, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../lib/tenant-context';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Upload, FileText, CheckCircle2, AlertCircle, ChevronRight, ArrowLeft, Rocket, LayoutDashboard, UtensilsCrossed, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseCsv, validateCsv, structureMenu, importMenu } from '../../lib/csv-import';
import type { ValidationResult, StructuredMenu, ImportResult } from '../../lib/csv-import';
type Step = 'upload' | 'validate' | 'preview' | 'confirm' | 'success';
const STEPS: {
  key: Step;
  label: string;
}[] = [{
  key: 'upload',
  label: 'Subir'
}, {
  key: 'validate',
  label: 'Validar'
}, {
  key: 'preview',
  label: 'Vista previa'
}, {
  key: 'confirm',
  label: 'Confirmar'
}, {
  key: 'success',
  label: 'Listo'
}];
export function ImportPage() {
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [menu, setMenu] = useState<StructuredMenu | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // ─── File handling ─────────────────────────────────────────
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.csv')) {
      alert('Solo se aceptan archivos .csv');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      setRawText(text);
      // Auto-advance to validation
      const rawRows = parseCsv(text);
      const validationResult = validateCsv(rawRows);
      setValidation(validationResult);
      setStep('validate');
    };
    reader.readAsText(file);
  }, []);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };
  // ─── Step transitions ──────────────────────────────────────
  const goToPreview = () => {
    if (!validation) return;
    const structured = structureMenu(validation.validRows);
    setMenu(structured);
    setStep('preview');
  };
  const goToConfirm = () => setStep('confirm');
  const handleImport = async () => {
    if (!menu || !restaurant) return;
    setImporting(true);
    const importResult = await importMenu(restaurant.id, menu);
    setResult(importResult);
    setImporting(false);
    if (importResult.success) {
      setStep('success');
    }
  };
  const reset = () => {
    setStep('upload');
    setFileName(null);
    setRawText(null);
    setValidation(null);
    setMenu(null);
    setResult(null);
  };
  // ─── Step index for progress ───────────────────────────────
  const currentStepIdx = STEPS.findIndex(s => s.key === step);
  // ─── Render ────────────────────────────────────────────────
  return <AdminLayout restaurant={restaurant} role={role} onSignOut={signOut} data-id="element-4551" data-mp="1zcm36yjjn0pg">
      <div className="max-w-3xl mx-auto px-4 py-8" data-id="element-4552" data-mp="24c8fn5rjscx7">
        {/* Header */}
        <div className="mb-8" data-id="element-4553" data-mp="24a5fvu0lf5fa">
          <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-4" data-id="element-4554" data-mp="292ou8t73u4l5">
            <ArrowLeft className="w-4 h-4" data-id="element-4555" data-mp="1gluln9pek3zz" />
            Volver al menú
          </button>
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-4556" data-mp="3iba7lq0p7h6z">
            Importar menú desde CSV
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-4557" data-mp="3ddi6q6i2skqn">
            Sube un archivo CSV con tu menú y lo crearemos automáticamente.
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1 mb-10" data-id="element-4558" data-mp="3naegsgnmhexb">
          {STEPS.map((s, idx) => <Fragment key={s.key} data-id="element-4559" data-mp="4wnd7hn64cp8" data-mpi={idx}>
              <div className="flex items-center gap-2" data-id="element-4560" data-mp="4vm077mldg4v">
                <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors', idx < currentStepIdx ? 'bg-green-500 text-white' : idx === currentStepIdx ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400')} data-id="element-4561" data-mp="vlmxqb8baze">
                  {idx < currentStepIdx ? <CheckCircle2 className="w-4 h-4" data-id="element-4562" data-mp="r5ce4t314kv1" /> : idx + 1}
                </div>
                <span className={cn('text-xs font-medium hidden sm:block', idx <= currentStepIdx ? 'text-gray-900' : 'text-gray-400')} data-id="element-4563" data-mp="w344hio0zyx4">
                  {s.label}
                </span>
              </div>
              {idx < STEPS.length - 1 && <div className={cn('flex-1 h-0.5 rounded-full mx-1', idx < currentStepIdx ? 'bg-green-500' : 'bg-gray-100')} data-id="element-4564" data-mp="ddxs0ddts3jr" />}
            </Fragment>)}
        </div>

        {/* ═══ STEP 1: UPLOAD ═══ */}
        {step === 'upload' && <div onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} className={cn('border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all', dragOver ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50')} data-id="element-4565" data-mp="1sv7lm8d0skmb">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} className="hidden" data-id="element-4566" data-mp="2f3d5047586em" />
            <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-4567" data-mp="25cidakb4oels">
              <Upload className="w-6 h-6 text-gray-400" data-id="element-4568" data-mp="21anhfvbyutfe" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1" data-id="element-4569" data-mp="2xu4k7mdtar0x">
              Arrastra tu archivo CSV aquí
            </p>
            <p className="text-sm text-gray-500 mb-6" data-id="element-4570" data-mp="2t22zdc2kvrza">
              o haz clic para seleccionar
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg" data-id="element-4571" data-mp="flt7az7e0qan">
              <FileText className="w-4 h-4" data-id="element-4572" data-mp="kl62q8cbr5ou" />
              Seleccionar archivo
            </div>

            <div className="mt-8 pt-6 border-t border-gray-100" data-id="element-4573" data-mp="on9i38246ufe">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3" data-id="element-4574" data-mp="tlkrjhohz2x3">
                Columnas requeridas
              </p>
              <div className="flex flex-wrap justify-center gap-2" data-id="element-4575" data-mp="13hxk2c8b3aq9">
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-4576" data-mp="13g2wsr9kb6j0">
                  category_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-4577" data-mp="1car2gij1uq8c">
                  item_name
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-4578" data-mp="1h7hjb82ohput">
                  price
                </span>
                <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded" data-id="element-4579" data-mp="x4qddpil8s08">
                  station_code
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-2" data-id="element-4580" data-mp="1667p6voyq7j4">
                Opcionales: description, option_name, option_price
              </p>
            </div>
          </div>}

        {/* ═══ STEP 2: VALIDATE ═══ */}
        {step === 'validate' && validation && <div className="space-y-6" data-id="element-4581" data-mp="ecxlxzj63eum">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-4" data-id="element-4582" data-mp="9h8frdcn6zpc">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5" data-id="element-4583" data-mp="n73lm2g6x5yu">
                <div className="flex items-center gap-2 mb-1" data-id="element-4584" data-mp="n96npkumapjx">
                  <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-4585" data-mp="if2dmsq221em" />
                  <span className="text-sm font-semibold text-green-800" data-id="element-4586" data-mp="3qvnetncg3q76">
                    Filas válidas
                  </span>
                </div>
                <p className="text-3xl font-black text-green-700" data-id="element-4587" data-mp="3msr22d9l6ob5">
                  {validation.validRows.length}
                </p>
              </div>
              <div className={cn('border rounded-xl p-5', validation.invalidRows.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200')} data-id="element-4588" data-mp="3hiomo0pmm4iv">
                <div className="flex items-center gap-2 mb-1" data-id="element-4589" data-mp="3vgv3e42g1nvb">
                  <AlertCircle className={cn('w-5 h-5', validation.invalidRows.length > 0 ? 'text-red-500' : 'text-gray-400')} data-id="element-4590" data-mp="3vfjy1ztco823" />
                  <span className={cn('text-sm font-semibold', validation.invalidRows.length > 0 ? 'text-red-800' : 'text-gray-600')} data-id="element-4591" data-mp="3qggsme9gjztl">
                    Filas con errores
                  </span>
                </div>
                <p className={cn('text-3xl font-black', validation.invalidRows.length > 0 ? 'text-red-600' : 'text-gray-400')} data-id="element-4592" data-mp="3aayovzprckh8">
                  {validation.invalidRows.length}
                </p>
              </div>
            </div>

            {/* Invalid rows detail */}
            {validation.invalidRows.length > 0 && <div className="bg-white border border-red-200 rounded-xl overflow-hidden" data-id="element-4593" data-mp="2cohtj00l4gy4">
                <div className="px-5 py-3 bg-red-50 border-b border-red-200" data-id="element-4594" data-mp="2hncikjp5b1qo">
                  <p className="text-sm font-semibold text-red-800" data-id="element-4595" data-mp="2hpfchetf5f0o">
                    Errores encontrados
                  </p>
                </div>
                <div className="max-h-60 overflow-y-auto divide-y divide-red-100" data-id="element-4596" data-mp="fuws828mjjm3">
                  {validation.invalidRows.map(row => <div key={row.row} className="px-5 py-3" data-id="element-4597" data-mp="20bxo80ot1lvx">
                      <div className="flex items-center gap-2 mb-1" data-id="element-4598" data-mp="1vg8itgq3tarp">
                        <span className="text-xs font-bold text-red-500 bg-red-100 px-2 py-0.5 rounded" data-id="element-4599" data-mp="2a4bpksbz0qhk">
                          Fila {row.row}
                        </span>
                      </div>
                      <ul className="space-y-0.5" data-id="element-4600" data-mp="2j3yqld0sfua3">
                        {row.errors.map((err, i) => <li key={i} className="text-xs text-red-700 flex items-center gap-1" data-id="element-4601" data-mp="2f3o9p5bgjjgg" data-mpi={i}>
                            <span data-id="element-4602" data-mp="2o3unksb2ilg5">•</span> {err}
                          </li>)}
                      </ul>
                    </div>)}
                </div>
              </div>}

            {/* File info */}
            {fileName && <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-200" data-id="element-4603" data-mp="2trt8q7herttb">
                <FileText className="w-5 h-5 text-gray-400" data-id="element-4604" data-mp="2yqmoy7o2xyx6" />
                <span className="text-sm text-gray-700 font-medium flex-1 truncate" data-id="element-4605" data-mp="2yt0ek0zzqixc">
                  {fileName}
                </span>
                <button onClick={reset} className="text-xs text-gray-400 hover:text-gray-600 transition-colors" data-id="element-4606" data-mp="37rcbslvd4eyr">
                  Cambiar archivo
                </button>
              </div>}

            {/* Actions */}
            <div className="flex gap-3" data-id="element-4607" data-mp="3mggv66dw8p8g">
              <button onClick={reset} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-4608" data-mp="3mhjqqrm5vfup">
                Volver
              </button>
              <button onClick={goToPreview} disabled={validation.validRows.length === 0} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold rounded-xl transition-colors', validation.validRows.length > 0 ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-gray-100 text-gray-400 cursor-not-allowed')} data-id="element-4609" data-mp="3vfc43q8bodfg">
                Continuar con {validation.validRows.length} filas
                <ChevronRight className="w-4 h-4" data-id="element-4610" data-mp="x4sbra62vgmg" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 3: PREVIEW ═══ */}
        {step === 'preview' && menu && <div className="space-y-6" data-id="element-4611" data-mp="ndxoq3l0cczy">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3" data-id="element-4612" data-mp="jef5rdcqybzr">
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-4613" data-mp="jbsyletrpyi2">
                <p className="text-2xl font-black text-gray-900" data-id="element-4614" data-mp="1fx307yzzjc11">
                  {menu.totalCategories}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-4615" data-mp="vn1rmi1boxb7">
                  Categorías
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-4616" data-mp="10n68i9657u1y">
                <p className="text-2xl font-black text-gray-900" data-id="element-4617" data-mp="mn56uri20ilf">
                  {menu.totalItems}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-4618" data-mp="qoh2periu022">
                  Productos
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4 text-center" data-id="element-4619" data-mp="1jcmnu2mhlr2f">
                <p className="text-2xl font-black text-gray-900" data-id="element-4620" data-mp="1jegxgksb5wr7">
                  {menu.totalOptions}
                </p>
                <p className="text-xs text-gray-500 font-medium mt-0.5" data-id="element-4621" data-mp="19mb87itfxtfp">
                  Opciones
                </p>
              </div>
            </div>

            {/* Category breakdown */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-id="element-4622" data-mp="222vr7fu0fovp">
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-200" data-id="element-4623" data-mp="3mtj5r3g10w4v">
                <p className="text-sm font-semibold text-gray-900" data-id="element-4624" data-mp="3mrgbu8br6iuv">
                  Vista previa del menú
                </p>
              </div>
              <div className="divide-y divide-gray-100 max-h-[28rem] overflow-y-auto" data-id="element-4625" data-mp="e7yw4sr3zcc">
                {menu.categories.map(cat => <div key={cat.name} className="px-5 py-4" data-id="element-4626" data-mp="3rl189vxp74ry">
                    <div className="flex items-center justify-between mb-3" data-id="element-4627" data-mp="33tvlsxx95e8o">
                      <h3 className="text-sm font-bold text-gray-900" data-id="element-4628" data-mp="2yvu0s7g9n4uk">
                        {cat.name}
                      </h3>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-4629" data-mp="3dtmeqv65zl6n">
                        {cat.items.length}{' '}
                        {cat.items.length === 1 ? 'producto' : 'productos'}
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-4630" data-mp="o3j1zsxkq5tn">
                      {cat.items.map(item => <div key={`${cat.name}-${item.name}`} className="flex items-start justify-between gap-4 py-1.5" data-id="element-4631" data-mp="v6qz2htazvym">
                          <div className="flex-1 min-w-0" data-id="element-4632" data-mp="154wy8rnq6nzs">
                            <p className="text-sm text-gray-800 font-medium truncate" data-id="element-4633" data-mp="155g6thrhk015">
                              {item.name}
                            </p>
                            {item.description && <p className="text-xs text-gray-400 truncate" data-id="element-4634" data-mp="ce4wlenw5752">
                                {item.description}
                              </p>}
                            {item.options.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4635" data-mp="r5dra26nhip2">
                                {item.options.map(opt => <span key={opt.name} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium" data-id="element-4636" data-mp="3puvtq1nl34li">
                                    {opt.name}
                                    {opt.price > 0 && ` +₡${opt.price}`}
                                  </span>)}
                              </div>}
                          </div>
                          <div className="text-right shrink-0" data-id="element-4637" data-mp="1t002aj9caclf">
                            <p className="text-sm font-bold text-gray-900" data-id="element-4638" data-mp="1t1jk4ean8vpb">
                              ₡{item.price.toLocaleString()}
                            </p>
                            <p className="text-[10px] text-gray-400 font-mono" data-id="element-4639" data-mp="1k8zrbuh19lce">
                              {item.station_code}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3" data-id="element-4640" data-mp="2u4bqw69oc7pj">
              <button onClick={() => setStep('validate')} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-4641" data-mp="2u5d3wga9349w">
                Volver
              </button>
              <button onClick={goToConfirm} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-4642" data-mp="2p4z2hh35msku">
                Todo se ve bien
                <ChevronRight className="w-4 h-4" data-id="element-4643" data-mp="21gfruuwmu3pl" />
              </button>
            </div>
          </div>}

        {/* ═══ STEP 4: CONFIRM ═══ */}
        {step === 'confirm' && menu && <div className="space-y-6" data-id="element-4644" data-mp="1ri9r4gmkb3mb">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center" data-id="element-4645" data-mp="1rjb9751zwqdj">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5" data-id="element-4646" data-mp="1wfrtfo62qklc">
                <span className="text-3xl" data-id="element-4647" data-mp="1cvt8sivguhwx">🚀</span>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4648" data-mp="18uhjkegi7yp9">
                ¿Crear el menú?
              </h2>
              <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6" data-id="element-4649" data-mp="1mua39ptm5enq">
                Se crearán {menu.totalCategories} categorías, {menu.totalItems}{' '}
                productos
                {menu.totalOptions > 0 && ` y ${menu.totalOptions} opciones`} en{' '}
                <strong data-id="element-4650" data-mp="1rriqw5xj6awd">{restaurant.name}</strong>.
              </p>

              {result && !result.success && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-left" data-id="element-4651" data-mp="25jgrtth390hg">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4652" data-mp="20lf9jarhl571">
                    <AlertCircle className="w-4 h-4 text-red-500" data-id="element-4653" data-mp="2fhczuceqbfbc" />
                    <span className="text-sm font-semibold text-red-800" data-id="element-4654" data-mp="2ffs8evnpochp">
                      Error durante la importación
                    </span>
                  </div>
                  <p className="text-xs text-red-700" data-id="element-4655" data-mp="2mzyl2vu89z5b">{result.error}</p>
                  <p className="text-xs text-red-500 mt-1" data-id="element-4656" data-mp="2i1nceofo5uoo">
                    Se crearon {result.categoriesCreated} categorías,{' '}
                    {result.itemsCreated} productos y {result.optionsCreated}{' '}
                    opciones antes del error.
                  </p>
                </div>}

              <div className="flex gap-3" data-id="element-4657" data-mp="24bju28jwul4i">
                <button onClick={() => setStep('preview')} disabled={importing} className="px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-4658" data-mp="1zcgzji1k3xas">
                  Volver
                </button>
                <button onClick={handleImport} disabled={importing} className={cn('flex-1 flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-bold rounded-xl transition-colors', importing ? 'bg-gray-300 text-gray-500 cursor-wait' : 'bg-green-600 text-white hover:bg-green-700')} data-id="element-4659" data-mp="1kmj4ci57m7c3">
                  {importing ? <>
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" data-id="element-4660" data-mp="11j7iodslli5i">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-4661" data-mp="1u624pf52cpl3" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-4662" data-mp="3u5xxokbcr5j" />
                      </svg>
                      Creando menú...
                    </> : <>
                      <Rocket className="w-4 h-4" data-id="element-4663" data-mp="21qmgp4va3pzo" />
                      Crear menú
                    </>}
                </button>
              </div>
            </div>
          </div>}

        {/* ═══ STEP 5: SUCCESS ═══ */}
        {step === 'success' && result && <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center" data-id="element-4664" data-mp="1nrd04435utv6">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4665" data-mp="1is0flpzrhyvr">
              <span className="text-4xl" data-id="element-4666" data-mp="1dt6zdpt3btrw">🚀</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4667" data-mp="1dv1nfczns207">
              ¡Tu menú está listo!
            </h2>
            <p className="text-sm text-gray-500 max-w-sm mx-auto mb-2" data-id="element-4668" data-mp="386ygfadr1fxd">
              Se crearon exitosamente:
            </p>
            <div className="flex justify-center gap-6 mb-8" data-id="element-4669" data-mp="3fr93lfunof7u">
              <div className="text-center" data-id="element-4670" data-mp="3fomu3ao7fpmz">
                <p className="text-2xl font-black text-green-600" data-id="element-4671" data-mp="3koipiliu68qx">
                  {result.categoriesCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-4672" data-mp="3ph2cg93eyg8e">categorías</p>
              </div>
              <div className="text-center" data-id="element-4673" data-mp="3ueu5x7jpiaep">
                <p className="text-2xl font-black text-green-600" data-id="element-4674" data-mp="2wrmu0y9f5l2g">
                  {result.itemsCreated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-4675" data-mp="31pwp78v58lo2">productos</p>
              </div>
              {result.optionsCreated > 0 && <div className="text-center" data-id="element-4676" data-mp="36oho94ynszax">
                  <p className="text-2xl font-black text-green-600" data-id="element-4677" data-mp="3bom3krntznzk">
                    {result.optionsCreated}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-4678" data-mp="lysc4zpcbrlx">opciones</p>
                </div>}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto" data-id="element-4679" data-mp="qwtutjiutowv">
              <button onClick={() => navigate(`/admin/${restaurant.slug}/menu`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-bold bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors" data-id="element-4680" data-mp="okrn4nshc4b0">
                <UtensilsCrossed className="w-4 h-4" data-id="element-4681" data-mp="ti1jyoi77i8v" />
                Ver menú
              </button>
              <button onClick={() => navigate(`/admin/${restaurant.slug}/dashboard`)} className="flex-1 flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors" data-id="element-4682" data-mp="17hsvmil8smjp">
                <LayoutDashboard className="w-4 h-4" data-id="element-4683" data-mp="1calxmypbtgs3" />
                Dashboard
              </button>
            </div>
          </div>}
      </div>
    </AdminLayout>;
}